// MediaLibraryPicker — the "reuse in context" modal (deck states 03 grid / 06
// list; design-handoff/concepts/media-library/03-builder-picker + 06-builder-picker-list).
// Shared, presentational component: opened from ANY surface that attaches media
// to a Segment/Wheel (DL-048, Gordon universal-access addendum 2026-07-14) so an
// already-uploaded asset is REUSED (nothing uploaded twice). Composes the same
// tabs/toolbar/grid/list as the Media Library page, so the two surfaces can never
// diverge. Data is fed in by MediaLibraryPickerConnected (no fetch here).
//
// Context type-filtering (addendum): `acceptedTypes` (from the consumer's existing
// type restriction) hides entirely-ineligible tabs, renders individually-ineligible
// items disabled with a plain-language reason, and blocks Insert for an ineligible
// selection (enforced in logic via canInsert, not just the disabled attribute).
//
// Picker-filter spec (Gordon 2026-07-24): the DEFAULT view goes further than
// disabling — items outside acceptedTypes are HIDDEN entirely (not just
// disabled), on every tab (all/captures included — the type filter composes
// with whichever tab is active, since 'captures' is a SOURCE filter and 'all'
// has no type filter of its own). A "Show all" toggle (default OFF, only
// rendered when the context actually restricts a type) flips that off: ON
// reveals the hidden items again, still disabled/grayed/non-selectable exactly
// as before. Toggle lives here (not in a per-consumer surface) so every
// consumer of this shared picker gets it automatically.
//
// EW-010 honesty: reuse is refcount-incrementing on the BE (WL-072); the caption
// chip states the contract ("Reused across Wheels — nothing is uploaded twice").
import React, { useMemo, useState } from "react";
import * as S from "./MediaLibrary.styled";
import { MediaGrid, MediaListTable } from "./MediaGrid";
import ErrorWheel from "../ErrorWheel";
import {
  IconClose, IconSearch, IconAllTiles, IconImage, IconVideo, IconAudio,
  IconDocument, IconGridLarge, IconList, IconTarget, IconCapture,
} from "./icons";
import { TYPE_TABS, filterMedia, sortMedia } from "../../pages/MediaLibrary/helpers";
import {
  visibleTypeTabs, defaultActiveTab, isItemEligible, ineligibleReason, canInsert,
  hasTypeRestriction,
} from "../SegmentBuilder/Shared/MediaSelection/librarySourceLogic";

const TAB_ICON = { all: IconAllTiles, images: IconImage, videos: IconVideo, audio: IconAudio, documents: IconDocument, captures: IconCapture };

export default function MediaLibraryPicker({
  open = true,
  items = [],
  segmentName = null,
  acceptedTypes = null,
  contextLabel = null,
  entryLabel = "From Media Library",
  status = "ready", // 'loading' | 'error' | 'ready'
  onRetry,
  onClose,
  onInsert,
}) {
  const tabs = useMemo(() => visibleTypeTabs(TYPE_TABS, acceptedTypes), [acceptedTypes]);
  const [activeType, setActiveType] = useState(() => defaultActiveTab(acceptedTypes));
  const [query, setQuery] = useState("");
  const [mode, setMode] = useState("large"); // 'large' (03) | 'list' (06)
  const [selectedId, setSelectedId] = useState(null);
  const [showAllTypes, setShowAllTypes] = useState(false); // default OFF — filtered view

  // Clamp to a visible tab (acceptedTypes could hide the current one).
  const effectiveType = tabs.some((t) => t.key === activeType) ? activeType : "all";
  const restrictsTypes = hasTypeRestriction(acceptedTypes);

  const tabFiltered = useMemo(
    () => sortMedia(filterMedia(items, { activeType: effectiveType, query }), "newest"),
    [items, effectiveType, query]
  );
  // Type-eligibility layer, composed on top of the tab/search filter above —
  // applies no matter which tab is active (all / captures / a single-type tab).
  // Default (showAllTypes false): hide anything outside acceptedTypes. Toggle
  // ON: keep everything; MediaGrid/MediaListTable render the rest disabled.
  const shown = useMemo(() => {
    if (!restrictsTypes || showAllTypes) return tabFiltered;
    return tabFiltered.filter((it) => isItemEligible(it, acceptedTypes));
  }, [tabFiltered, restrictsTypes, showAllTypes, acceptedTypes]);
  const hiddenCount = tabFiltered.length - shown.length;
  if (!open) return null;

  const selectedItem = selectedId != null ? shown.find((i) => i.id === selectedId) : null;
  const selectedIds = selectedItem ? [selectedItem.id] : [];
  const eligible = (item) => isItemEligible(item, acceptedTypes);
  const toggle = (item) => {
    if (!eligible(item)) return; // insert-guard belt-and-suspenders
    setSelectedId((cur) => (cur === item.id ? null : item.id));
  };
  const insertAllowed = canInsert(selectedItem, acceptedTypes);
  const doInsert = () => {
    if (selectedItem && insertAllowed && onInsert) onInsert(selectedItem);
  };

  return (
    <S.Backdrop role="dialog" aria-modal="true" aria-label="Media Library picker" data-testid="media-picker" onClick={(e) => e.target === e.currentTarget && onClose && onClose()}>
      <S.Modal w={768}>
        <S.ModalHead>
          <div>
            <div className="t">Media Library</div>
            <div className="sub">Pick anything you have already uploaded — it stays in your Library.</div>
          </div>
          <button type="button" className="x" aria-label="Close" onClick={onClose}><IconClose size={20} /></button>
        </S.ModalHead>

        <S.ModalBody>
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 16 }}>
            <S.Chip>{entryLabel}</S.Chip>
            <S.Chip tone="target">
              <IconTarget size={15} /> Inserts into the open Segment{segmentName ? <>: <b>{segmentName}</b></> : ""}
            </S.Chip>
          </div>

          <S.ControlsRow style={{ borderBottom: "none", marginBottom: 16, paddingBottom: 0 }}>
            <S.Tabs role="tablist" aria-label="Media type">
              {tabs.map((t) => {
                const Ico = TAB_ICON[t.key];
                return (
                  <S.Tab key={t.key} role="tab" aria-selected={effectiveType === t.key} active={effectiveType === t.key ? 1 : 0} onClick={() => setActiveType(t.key)}>
                    <Ico size={18} /> {t.label}
                  </S.Tab>
                );
              })}
            </S.Tabs>
            <S.RightControls>
              {restrictsTypes && (
                <S.ShowAllToggle>
                  <span style={{ position: "relative", display: "inline-flex" }}>
                    <S.ShowAllInput
                      type="checkbox"
                      role="switch"
                      checked={showAllTypes}
                      aria-checked={showAllTypes}
                      onChange={(e) => setShowAllTypes(e.target.checked)}
                      aria-label="Show all media types, including ones that can't be used here"
                      data-testid="picker-show-all-toggle"
                    />
                    <S.ShowAllTrack checked={showAllTypes ? 1 : 0} aria-hidden="true" />
                  </span>
                  Show all{!showAllTypes && hiddenCount > 0 ? ` (${hiddenCount} hidden)` : ""}
                </S.ShowAllToggle>
              )}
              <S.SearchBox>
                <IconSearch size={18} />
                <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search media" aria-label="Search media" />
              </S.SearchBox>
              <S.ViewToggle role="group" aria-label="View">
                <S.ViewSeg active={mode === "large" ? 1 : 0} aria-pressed={mode === "large"} aria-label="Grid view" onClick={() => setMode("large")}><IconGridLarge size={18} /></S.ViewSeg>
                <S.ViewSeg active={mode === "list" ? 1 : 0} aria-pressed={mode === "list"} aria-label="List view" onClick={() => setMode("list")}><IconList size={18} /></S.ViewSeg>
              </S.ViewToggle>
            </S.RightControls>
          </S.ControlsRow>

          <div style={{ maxHeight: "42vh", overflow: "auto", paddingBottom: 8 }} aria-busy={status === "loading"}>
            {status === "loading" ? (
              <S.Grid cols={4} data-testid="picker-loading">
                {Array.from({ length: 8 }).map((_, i) => (
                  <S.Skeleton key={i} style={{ aspectRatio: "16 / 10" }} aria-hidden="true" />
                ))}
              </S.Grid>
            ) : status === "error" ? (
              <S.ErrorWrap data-testid="picker-error" role="alert">
                {/* Shared ErrorWheel. This branch sits directly above an EMPTY
                    branch using the same shell, so the mark is what tells a
                    broken load apart from an empty one. */}
                <ErrorWheel variant="section" decorative style={{ marginBottom: 16 }} />
                <div className="h">Couldn’t load your library</div>
                <div className="p">Something went wrong reaching your media.</div>
                <S.RetryButton type="button" onClick={onRetry}>Retry</S.RetryButton>
              </S.ErrorWrap>
            ) : shown.length === 0 ? (
              <S.ErrorWrap data-testid="picker-empty">
                {restrictsTypes && !showAllTypes && tabFiltered.length > 0 ? (
                  <>
                    <div className="h">No eligible media here</div>
                    <div className="p">
                      Everything in this view can’t be used {contextLabel ? `as ${contextLabel}` : "here"}.
                      Turn on “Show all” to see it (grayed out).
                    </div>
                  </>
                ) : (
                  <>
                    <div className="h">Nothing here yet</div>
                    <div className="p">Upload media from the Library, then reuse it here.</div>
                  </>
                )}
              </S.ErrorWrap>
            ) : mode === "list" ? (
              <MediaListTable
                items={shown}
                selectedIds={selectedIds}
                onActivate={toggle}
                showKebab={false}
                isDisabled={(item) => !eligible(item)}
                disabledReason={(item) => ineligibleReason(item, contextLabel)}
              />
            ) : (
              <MediaGrid
                items={shown}
                cols={4}
                compact
                selectedIds={selectedIds}
                showHoverPill={false}
                onActivate={toggle}
                isDisabled={(item) => !eligible(item)}
                disabledReason={(item) => ineligibleReason(item, contextLabel)}
              />
            )}
          </div>
        </S.ModalBody>

        <S.ModalFoot style={{ justifyContent: "space-between" }}>
          <S.CaptionChip><IconTarget size={15} /> Reused across Wheels — nothing is uploaded twice</S.CaptionChip>
          <div style={{ display: "flex", gap: 12 }}>
            <S.GhostButton onClick={onClose}>Cancel</S.GhostButton>
            <S.PrimaryButton
              disabled={!selectedItem || !insertAllowed}
              style={!selectedItem || !insertAllowed ? { opacity: 0.5, cursor: "not-allowed" } : undefined}
              onClick={doInsert}
            >
              Insert into Segment
            </S.PrimaryButton>
          </div>
        </S.ModalFoot>
      </S.Modal>
    </S.Backdrop>
  );
}
